import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import LegalFooter from "../components/LegalFooter";

export default function Privacy() {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground" data-testid="privacy-page">
      <header className="px-6 py-5 border-b border-border/60 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          data-testid="privacy-back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Link>
        <span className="brand-wordmark text-sm">FINALLOOK</span>
      </header>

      <main className="flex-1 px-6 py-10">
        <article className="max-w-2xl mx-auto space-y-6 text-sm leading-relaxed">
          <div>
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mb-2" data-testid="privacy-title">
              Privacy Policy
            </h1>
            <p className="text-xs text-muted-foreground" data-testid="privacy-effective-date">
              Effective date: February 20, 2026
            </p>
          </div>

          <section>
            <p>
              FINALLOOK ("FINALLOOK," "we," "us," or "our") provides an AI-powered communication
              review service that helps you evaluate the tone and clarity of text messages before
              you send them. This Privacy Policy explains what information we collect, how we use
              it, and the choices you have. By using FINALLOOK you agree to the practices described
              here.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">1. Information We Collect</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Account information:</strong> the email address and password (stored only
                as a bcrypt hash) you provide when you create an account.
              </li>
              <li>
                <strong>Message content you submit for review:</strong> the draft message, intended
                recipient description, and communication goal you enter into the composer. This
                content is sent to our AI model provider (Anthropic) for the sole purpose of
                generating a review for you.
              </li>
              <li>
                <strong>Delivery information:</strong> when you explicitly approve a message to be
                sent as an SMS, we collect the destination phone number, the final message text,
                and the delivery-status updates returned by our SMS provider (Twilio).
              </li>
              <li>
                <strong>Product usage:</strong> account timestamps, review counts, quota usage, and
                delivery history so we can operate the service and enforce fair-use limits.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">2. How We Use Information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide, maintain, and improve the FINALLOOK service.</li>
              <li>Generate AI reviews of the drafts you submit.</li>
              <li>Send SMS messages to the recipients you explicitly approve.</li>
              <li>Send transactional emails such as password-reset links.</li>
              <li>Enforce usage quotas, prevent abuse, and secure your account.</li>
              <li>Comply with legal obligations and respond to lawful requests.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">3. SMS Messaging &amp; Consent</h2>
            <p className="mb-2">
              FINALLOOK only sends an SMS after you personally review the draft and tap the
              approval button in the app. That explicit tap is your opt-in for that specific
              message. FINALLOOK does not send marketing SMS, promotional broadcasts, or
              autoresponders.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Message frequency:</strong> messages are sent only when you approve them —
                there is no recurring schedule.
              </li>
              <li>
                <strong>Message and data rates:</strong> standard message and data rates from your
                mobile carrier may apply to messages sent from your FINALLOOK account and to any
                replies from the recipient.
              </li>
              <li>
                <strong>Opt-out:</strong> a recipient can stop receiving messages from your
                FINALLOOK-connected number at any time by replying <strong>STOP</strong> to that
                number. Replying <strong>HELP</strong> returns basic information about the sender.
              </li>
              <li>
                <strong>Supported carriers:</strong> we deliver SMS through Twilio, which supports
                all major U.S. carriers. Carriers are not liable for delayed or undelivered
                messages.
              </li>
            </ul>
          </section>

          <section className="rounded-lg border border-border/70 p-4 bg-secondary/30">
            <h2 className="font-heading font-semibold text-lg mb-2">
              4. No Sale or Sharing of Phone Numbers or Opt-In Data
            </h2>
            <p>
              <strong>
                FINALLOOK does not sell, rent, lease, or share phone numbers, SMS opt-in
                information, or SMS message content with any third party for marketing or
                promotional purposes.
              </strong>{" "}
              Opt-in consent and the associated phone numbers are used exclusively to deliver the
              specific SMS you approve inside the app.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">5. Service Providers</h2>
            <p className="mb-2">
              We rely on a small number of vetted service providers, each of which processes data
              only to perform services for us:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Anthropic</strong> — generates AI reviews of the drafts you submit.
              </li>
              <li>
                <strong>Twilio</strong> — delivers SMS messages you approve and returns delivery
                status.
              </li>
              <li>
                <strong>Resend</strong> — delivers transactional email such as password-reset
                links.
              </li>
              <li>
                <strong>MongoDB</strong> — hosts our application database.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">6. Data Retention</h2>
            <p>
              We retain account information and reviews for as long as your account is active. You
              can delete individual reviews from the History screen. You can request full deletion
              of your account and associated data by contacting us at the address below.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">7. Your Rights</h2>
            <p>
              Depending on where you live, you may have the right to access, correct, delete, or
              export the personal information we hold about you, and to object to or restrict
              certain processing. To exercise these rights, contact us using the details below.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">8. Security</h2>
            <p>
              Passwords are stored only as one-way bcrypt hashes. All traffic between your device
              and our servers is encrypted in transit. No system is perfectly secure, but we take
              reasonable technical and organizational measures to protect your information.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">9. Children</h2>
            <p>
              FINALLOOK is not directed to children under 13, and we do not knowingly collect
              personal information from anyone under 13. If you believe a child has provided us
              with personal information, contact us and we will delete it.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we will revise the
              "Effective date" at the top of this page. Material changes will be communicated in
              the app or by email.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-lg mb-2">11. Contact Us</h2>
            <p>
              Questions or requests about this Privacy Policy? Email us at{" "}
              <a
                href="mailto:gregory@finallookapp.com"
                className="text-primary hover:underline"
                data-testid="privacy-contact-email"
              >
                gregory@finallookapp.com
              </a>
              .
            </p>
          </section>
        </article>
      </main>

      <LegalFooter />
    </div>
  );
}
